import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownWideNarrow, Box, ChartLine, Gem, LayoutDashboard, Menu, ScrollText, Ticket, Users, UsersRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { api } from '../lib/api';
import { meKey, useMe } from '../lib/session';
import { applyTheme, getTheme, type ThemeChoice } from '../lib/theme';
import { BrandMark } from '../ui/brand';
import { Button } from '../ui/controls';
import { ErrorNotice, Notice } from '../ui/feedback';
import { LoginSection } from '../portal/sections';
import { PortalLayout } from '../portal/PortalLayout';
import { GlobalSearch } from './GlobalSearch';
import { paths } from './paths';

/**
 * Admin-Rahmen (design.md §5): Seitenleiste 216 px mit Gruppen „Daten“ und „Verwaltung“, Kopf mit globaler Suche
 * und Kontomenü. < 1200 px Iconleiste, < 768 px Schublade. Zugriff nur für Admins (Server prüft ohnehin).
 */

const NAV = [
  {
    label: 'Daten',
    items: [
      { to: paths.overview, label: 'Übersicht', icon: LayoutDashboard, end: true },
      { to: paths.players, label: 'Spieler', icon: Users },
      { to: paths.teams, label: 'Teams', icon: UsersRound },
      { to: paths.chests, label: 'Kisten', icon: Box },
      { to: paths.items, label: 'Items', icon: Gem },
      { to: paths.sortPlans, label: 'Sortierpläne', icon: ArrowDownWideNarrow },
    ],
  },
  {
    label: 'Verwaltung',
    items: [
      { to: paths.betaCodes, label: 'Beta-Codes', icon: Ticket },
      { to: paths.logs, label: 'Protokolle', icon: ScrollText },
      { to: paths.stats, label: 'Statistik', icon: ChartLine },
    ],
  },
];

export function AdminLayout() {
  const me = useMe();
  if (me.isPending) return <p className="muted portal">Lädt…</p>;
  if (me.isError)
    return (
      <PortalLayout>
        <ErrorNotice error={me.error} onRetry={() => me.refetch()} />
      </PortalLayout>
    );
  if (me.data === null)
    return (
      <PortalLayout>
        <LoginSection returnTo={window.location.pathname} />
      </PortalLayout>
    );
  if (!me.data.isAdmin || me.data.status !== 'active')
    return (
      <PortalLayout>
        <Notice>Dieser Bereich ist nur für Admins.</Notice>
        <p>
          <Link to="/">Zu deinem Konto</Link>
        </p>
      </PortalLayout>
    );
  return <Shell name={me.data.name ?? 'Admin'} />;
}

function Shell({ name }: { name: string }) {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();
  useEffect(() => setDrawer(false), [location.pathname]);

  return (
    <div className="shell">
      <aside className={`side${drawer ? ' open' : ''}`} aria-label="Navigation">
        <div className="brand">
          <BrandMark />
          <span>Chest Catalog</span>
        </div>
        {NAV.map((g) => (
          <nav key={g.label} className="nav-group nav" aria-label={g.label}>
            <div className="nav-label">{g.label}</div>
            {g.items.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} title={label}>
                <Icon aria-hidden strokeWidth={1.5} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        ))}
        <div className="side-foot">
          <span className={`env${import.meta.env.DEV ? ' dev' : ''}`}>{import.meta.env.DEV ? 'DEV' : 'PROD'}</span> · v
          {__APP_VERSION__}
        </div>
      </aside>
      {drawer && <div className="scrim" onClick={() => setDrawer(false)} aria-hidden />}
      <div className="main-col">
        <header className="top">
          <button type="button" className="menu-btn" aria-label={drawer ? 'Menü schließen' : 'Menü öffnen'} onClick={() => setDrawer((d) => !d)}>
            {drawer ? <X aria-hidden /> : <Menu aria-hidden />}
          </button>
          <GlobalSearch />
          <AccountMenu name={name} />
        </header>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AccountMenu({ name }: { name: string }) {
  const qc = useQueryClient();
  const [theme, setTheme] = useState<ThemeChoice>(getTheme());
  const logout = useMutation({
    mutationFn: () => api.post('/web/auth/logout'),
    onSuccess: () => qc.setQueryData(meKey, null),
  });
  return (
    <div className="me">
      <span className="who">{name}</span>
      <select
        aria-label="Darstellung"
        value={theme}
        onChange={(e) => {
          const v = e.target.value as ThemeChoice;
          setTheme(v);
          applyTheme(v);
        }}
      >
        <option value="system">System</option>
        <option value="light">Hell</option>
        <option value="dark">Dunkel</option>
      </select>
      <Link to="/" className="hide-sm">
        Portal
      </Link>
      <Button variant="quiet" onClick={() => logout.mutate()} busy={logout.isPending} busyText="Abmelden…">
        Abmelden
      </Button>
    </div>
  );
}
